import { BellRing, BrainCircuit, CalendarDays, FileCheck2, Globe2, Home, Layers3, MoreHorizontal, LogOut, House, Cpu, Camera, ShieldCheck, Zap, Car, Bot, Settings, ListTodo, FolderOpen, MessageSquare, MessageSquareText, Workflow, Sparkles } from "lucide-react";
import { AionLogoArtwork } from "@/components/aion/AionLogoArtwork";
import { Button } from "@/components/ui/button";

const NAV_ITEMS = [
  { id: "home", label: "Home", icon: Home },
  { id: "aion", label: "AION", icon: BrainCircuit },
  { id: "brain", label: "Brain", icon: BrainCircuit },
  { id: "conversations", label: "Gespräche", icon: MessageSquareText },
  { id: "today", label: "Heute", icon: CalendarDays },
  { id: "briefing", label: "Live", icon: Globe2 },
  { id: "notifications", label: "Hinweise", icon: BellRing },
  { id: "decisions", label: "Entscheidungen", icon: FileCheck2 },
  { id: "memory", label: "Memory", icon: Layers3 },
  { id: "rooms", view: "more", section: "spatial", label: "Räume", icon: House },
  { id: "devices", view: "more", section: "devices", label: "Geräte", icon: Cpu },
  { id: "scenes", view: "more", section: "scenes", label: "Szenen", icon: Sparkles },
  { id: "automations", view: "more", section: "automations", label: "Automationen", icon: Workflow },
  { id: "cameras", view: "more", section: "cameras", label: "Kameras", icon: Camera },
  { id: "security", view: "more", section: "privacy", label: "Sicherheit", icon: ShieldCheck },
  { id: "energy", view: "more", section: "energy", label: "Energie", icon: Zap },
  { id: "vehicle", view: "more", section: "vehicle", label: "Fahrzeug", icon: Car },
  { id: "robotics", view: "more", section: "robotics", label: "Roboter", icon: Bot },
  { id: "communication", view: "more", section: "communication", label: "Kommunikation", icon: MessageSquare },
  { id: "appointments", view: "more", section: "appointments", label: "Termine", icon: CalendarDays },
  { id: "tasks", view: "more", section: "tasks", label: "Aufgaben", icon: ListTodo },
  { id: "projects", label: "Projekte", icon: FolderOpen },
  { id: "settings", view: "more", section: "settings", label: "Einstellungen", icon: Settings },
  { id: "more", label: "Mehr", icon: MoreHorizontal },
];
const MOBILE_NAV_ITEMS = NAV_ITEMS.filter((item) => ["home", "aion", "brain", "briefing", "conversations", "today", "memory", "more"].includes(item.id));

const roleLabel = (user) => {
  if (user?.account_role) return String(user.account_role).replaceAll("_", " ");
  return user?.role === "admin" ? "Admin" : "User";
};

export const AionAppShell = ({ activeView, activeSection, onViewChange, onMoreSectionChange, user, onLogout, actionBar, children, activeProfile, familyProfiles, onProfileSwitch, profileSwitchPending, navBadges = {} }) => (
  <div className={`aion-app-shell${actionBar ? " aion-app-shell-with-action-bar" : ""}`} data-testid="aion-app-shell" data-active-view={activeView}>
    <aside className="aion-sidebar" data-testid="aion-sidebar">
      <div className="aion-brand-block" data-testid="aion-brand-block">
        <AionLogoArtwork
          variant="main"
          className="aion-brand-logo-wrap"
          imageClassName="aion-brand-logo"
          dataTestId="aion-sidebar-main-logo"
        />
        <div className="aion-brand-meta" data-testid="aion-brand-meta">
          <div className="aion-brand-pill" data-testid="aion-brand-pill">AION PRO</div>
          <h1 className="aion-brand-title" data-testid="aion-brand-title">AI Operating System</h1>
          <p className="aion-brand-copy" data-testid="aion-brand-copy">Intelligent. Persönlich. Vernetzt.</p>
        </div>
      </div>

      <nav className="aion-sidebar-nav" data-testid="aion-sidebar-nav">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              aria-current={activeView === (item.view || item.id) && (item.section ? activeSection === item.section : activeView !== "more") ? "page" : undefined}
              aria-label={Number(navBadges?.[item.id] || 0) > 0 ? `${item.label}, ${Number(navBadges[item.id])} offen` : item.label}
              className="aion-nav-item"
              onClick={() => item.section ? onMoreSectionChange?.(item.section) : onViewChange(item.view || item.id)}
              data-testid={`nav-${item.id}-button`}
            >
              {item.id === "aion" ? (
                <AionLogoArtwork
                  variant="compact"
                  className="aion-nav-brand-icon-wrap"
                  imageClassName="aion-nav-brand-icon"
                  dataTestId="aion-sidebar-compact-logo"
                />
              ) : (
                <Icon className="aion-nav-icon" aria-hidden="true" />
              )}
              <span>{item.label}</span>
              {Number(navBadges?.[item.id] || 0) > 0 ? (
                <span className="aion-nav-badge" data-testid={`nav-${item.id}-badge`}>
                  {Number(navBadges[item.id]) > 99 ? "99+" : Number(navBadges[item.id])}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <div className="aion-sidebar-footer" data-testid="aion-sidebar-footer">
        <div className="aion-user-card" data-testid="aion-user-card">
          <span data-testid="aion-user-role">{roleLabel(user)}</span>
          <strong data-testid="aion-user-name">{user?.name || user?.email || "AION User"}</strong>
          <small data-testid="aion-user-email">{user?.email || ""}</small>
        </div>
        <Button variant="outline" onClick={onLogout} data-testid="aion-logout-button">
          <LogOut className="aion-button-icon" aria-hidden="true" />
          Abmelden
        </Button>
      </div>
    </aside>

    <main className={`aion-main${actionBar ? " aion-main-with-action-bar" : ""}`} data-testid="aion-main">
      <header className="aion-topbar" data-testid="aion-topbar">
        <div className="aion-topbar-heading" data-testid="aion-topbar-heading">
          <p className="aion-topbar-kicker" data-testid="aion-topbar-kicker">AION CORE</p>
          <h2 className="aion-topbar-title" data-testid="aion-topbar-title">{NAV_ITEMS.find((item) => item.section ? activeView === "more" && item.section === activeSection : item.id === activeView)?.label || "AION"}</h2>
          <button type="button" className="aion-topbar-search" onClick={() => onViewChange("aion")} data-testid="aion-topbar-search">Frag AION etwas …</button>
        </div>
        <div className="aion-topbar-status" data-testid="aion-topbar-status">
          <AionLogoArtwork
            variant="compact"
            className="aion-topbar-status-icon-wrap"
            imageClassName="aion-topbar-status-icon"
            dataTestId="aion-topbar-compact-logo"
          />
          <div className="aion-topbar-profile-block" data-testid="aion-topbar-profile-block">
            <span className="aion-topbar-profile-mode" data-testid="aion-topbar-profile-mode">{activeProfile?.access_mode === "shared_view" ? "Gemeinsamer Bereich" : "Privater Bereich"}</span>
            <strong className="aion-topbar-profile-name" data-testid="aion-topbar-profile-name">{activeProfile?.name || user?.name || "AION User"}</strong>
          </div>
          {familyProfiles?.length ? (
            <select
              className="aion-profile-select"
              value={activeProfile?.user_id || user?.id || ""}
              onChange={(event) => onProfileSwitch?.(event.target.value)}
              disabled={Boolean(profileSwitchPending)}
              data-testid="aion-profile-switch-select"
            >
              {familyProfiles.map((profile) => (
                <option key={profile.user_id} value={profile.user_id}>
                  {profile.name} · {profile.access_mode === "shared_view" ? "Gemeinsam" : "Privat"}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </header>

      <div className="aion-main-content" data-testid="aion-main-content" data-active-view={activeView} key={activeView}>{children}</div>
    </main>

    {actionBar}

    <nav className="aion-mobile-nav" data-testid="aion-mobile-nav">
      {MOBILE_NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            aria-current={activeView === item.id ? "page" : undefined}
            aria-label={Number(navBadges?.[item.id] || 0) > 0 ? `${item.label}, ${Number(navBadges[item.id])} offen` : item.label}
            className="aion-mobile-nav-item"
            onClick={() => onViewChange(item.id)}
            data-testid={`mobile-nav-${item.id}-button`}
          >
            {item.id === "aion" ? (
              <AionLogoArtwork
                variant="compact"
                className="aion-mobile-brand-icon-wrap"
                imageClassName="aion-mobile-brand-icon"
                dataTestId="aion-mobile-compact-logo"
              />
            ) : (
              <Icon className="aion-nav-icon" aria-hidden="true" />
            )}
            <span>{item.label}</span>
            {Number(navBadges?.[item.id] || 0) > 0 ? (
              <span className="aion-mobile-nav-badge" data-testid={`mobile-nav-${item.id}-badge`}>
                {Number(navBadges[item.id]) > 99 ? "99+" : Number(navBadges[item.id])}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  </div>
);
